/**
 * Sincronização (drill-down de "falhas de sincronização").
 *
 * Hoje lista os alertas operacionais de integração (`/api/admin/notifications`)
 * — o registro real que originou o KPI, com a entidade relacionada quando o
 * alerta a informa.
 *
 * A Task 11 acrescentou, nesta MESMA tela, as divergências Bling↔local item a
 * item (segunda seção, dataset de catálogo). Elas NÃO entram na contagem do
 * KPI de falhas de sincronização: o KPI continua sendo "alertas de integração
 * não lidos" e seu drill-down continua sendo exatamente aquele conjunto de
 * registros. Misturar os dois faria o card somar coisas de naturezas
 * diferentes; a divergência tem a sua própria tela, com a sua própria
 * contagem (`/divergencias`).
 *
 * Esta tela é também o DESTINO do link "evento de sync" de cada divergência
 * (`/sincronizacao?syncLog=<id>`): com esse parâmetro ela exibe o registro de
 * `bling_sync_log` indicado e restringe as divergências àquelas que aquele
 * evento originou. Um link de drill-down que caísse numa tela genérica não
 * seria drill-down — seria só uma URL.
 *
 * Migrado para `.tsx`/JSX real (Cluster D — Logística & Garantias, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * apenas (`createElement` → JSX): a tela já estava tokenizada e já tinha os
 * ícones `Ban`/`CheckCircle2`/`TriangleAlert` no selo de resultado do evento
 * de sync (achado da investigação inicial do cluster) — nenhum ícone novo foi
 * adicionado. `CatalogBoundary`/`Divergencias.tsx` são consumidos via import,
 * sem edição (componentes compartilhados com o Cluster B — Catálogo & Estoque,
 * já mergeado). Nenhum `data-*`/aria/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `FulfillmentStateMachine.tsx`.
import React, { type ReactElement, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Ban, CheckCircle2, type LucideIcon, TriangleAlert } from "lucide-react";
import { RecordTable } from "../components/RecordTable.js";
import { DatasetBoundary } from "../components/DatasetBoundary.js";
import { CatalogBoundary } from "../components/CatalogBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { DrillDownHeader } from "../components/DrillDownHeader.js";
import { DivergenciasTable } from "./Divergencias.js";
import { Badge, type BadgeTone } from "../ui/index.js";
import { blingSourceLabel, catalogDivergencesOf, syncLogEntryById, type CatalogDataset } from "../catalog/dataset.js";
import { divergencesForSyncLog, summarizeDivergences } from "../../../../packages/catalog/src/index.js";
import { notificationsForKpi, parseKpiId } from "../dashboard/drilldown.js";
import { formatDateTime } from "../format.js";
import type { DashboardDataset } from "../dashboard/kpis.js";
import type { BlingSyncLogEntry, PlatformNotification } from "../api/types.js";
import type { DrillDownViewProps } from "../dashboard/view-props.js";

export interface SincronizacaoViewProps extends DrillDownViewProps {
  /**
   * Seção de divergências de catálogo (Task 11). Injetada pela página porque
   * vem de OUTRO dataset (catálogo) — assim esta view continua renderizável em
   * teste com o dataset do dashboard apenas.
   */
  divergencesSection?: ReactNode;
  /** `?syncLog=` — presente quando se chegou pelo link de uma divergência. */
  syncLogId?: string | null;
}

/** Lê `?syncLog=`; vazio vira `null` (tela completa, sem recorte). */
export function parseSyncLogId(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}

/** Severidade do alerta como selo (texto sempre presente; a cor só reforça). */
const SEVERITY_TONE: Record<string, BadgeTone> = { critical: "danger", warning: "warning", info: "info" };

const SYNC_STATUS_LABELS: Record<BlingSyncLogEntry["status"], string> = {
  synced: "sincronizado",
  conflict: "conflito",
  failed: "falhou"
};

const SYNC_DIRECTION_LABELS: Record<BlingSyncLogEntry["direction"], string> = {
  pull: "leitura do Bling (pull)",
  push: "envio ao Bling (push)"
};

const SYNC_STATUS_META: Record<BlingSyncLogEntry["status"], { tone: string; Icon: LucideIcon }> = {
  synced: { tone: "border-success text-success", Icon: CheckCircle2 },
  conflict: { tone: "border-warning text-warning", Icon: TriangleAlert },
  failed: { tone: "border-danger text-danger", Icon: Ban }
};

/**
 * Resultado do evento de sync como ícone + texto (régua §2). `data-sync-status`
 * e o rótulo (SYNC_STATUS_LABELS) seguem no wrapper — hooks dos testes.
 */
function syncStatusBadge(status: BlingSyncLogEntry["status"]): ReactElement {
  const meta = SYNC_STATUS_META[status];
  const Icon = meta.Icon;
  return (
    <span
      className={"inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " + meta.tone}
      data-sync-status={status}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden focusable={false} />
      <span>{SYNC_STATUS_LABELS[status]}</span>
    </span>
  );
}

/** O evento de `bling_sync_log` que a divergência citou, como registro-fonte. */
function syncLogBlock(dataset: CatalogDataset, syncLogId: string): ReactElement {
  const entry = syncLogEntryById(dataset, syncLogId);
  const title = `Evento de sync de origem: ${syncLogId}`;
  if (entry === "source_absent") {
    return (
      <section
        className="page__section flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1"
        data-section="evento-sync"
        data-sync-log={syncLogId}
      >
        <h3 className="text-lg font-extrabold text-ink">{title}</h3>
        <StatusPanel
          kind="awaiting_source"
          title="Eventos de sincronização não publicados neste ambiente"
          detail="A rota /api/catalog/bling/sync-log (tabela bling_sync_log) não respondeu aqui. O identificador do evento continua visível na divergência, mas o registro em si não pode ser exibido — e dizer que ele não existe seria errado."
        />
      </section>
    );
  }
  if (entry === "not_found") {
    return (
      <section
        className="page__section flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1"
        data-section="evento-sync"
        data-sync-log={syncLogId}
      >
        <h3 className="text-lg font-extrabold text-ink">{title}</h3>
        <StatusPanel
          kind="empty"
          title="Este evento não está entre os eventos de sync publicados"
          detail={`O estado operacional cita ${syncLogId}, mas a rota de eventos não devolve esse registro. As divergências abaixo continuam sendo as que apontam para ele.`}
        />
      </section>
    );
  }
  return (
    <section
      className="page__section flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1"
      data-section="evento-sync"
      data-sync-log={syncLogId}
    >
      <h3 className="text-lg font-extrabold text-ink">{title}</h3>
      <RecordTable<BlingSyncLogEntry>
        caption="Registro de bling_sync_log que originou o estado operacional comparado"
        rows={[entry]}
        idOf={(row) => row.id}
        emptyMessage="Nenhum evento de sync para este identificador."
        // Fase 10 (Plano de Ataque R8): rollout mecânico do primitivo da Fase 6.
        animateRows
        columns={[
          { key: "id", label: "Evento", render: (row) => row.id },
          { key: "entity", label: "Entidade", render: (row) => row.entity_type },
          { key: "localId", label: "Registro local", render: (row) => row.local_id },
          { key: "blingId", label: "Registro no Bling", render: (row) => row.bling_id ?? "sem par no Bling" },
          { key: "direction", label: "Direção", render: (row) => SYNC_DIRECTION_LABELS[row.direction] },
          {
            key: "status",
            label: "Resultado",
            render: (row) => syncStatusBadge(row.status)
          },
          { key: "createdAt", label: "Quando", render: (row) => formatDateTime(row.created_at) }
        ]}
      />
    </section>
  );
}

export interface SincronizacaoDivergenciasProps {
  dataset: CatalogDataset;
  /** `?syncLog=`; `null` = fila inteira de pendências. */
  syncLogId?: string | null;
}

/**
 * Divergências de catálogo nesta tela.
 *
 * Sem recorte: só o que é fila de trabalho (campo de autoridade Bling defasado)
 * e vínculo ausente — as divergências ESPERADAS (campos da Plataforma) ficam na
 * tela dedicada, para a fila de sincronização não parecer maior do que é.
 *
 * Com `syncLogId`: o recorte é o evento citado pela divergência — o registro de
 * origem aparece acima e a tabela mostra TODAS as divergências daquele evento
 * (inclusive as esperadas), porque aqui a pergunta é "o que este evento
 * explica?", não "o que falta tratar?".
 */
export function SincronizacaoDivergenciasView({
  dataset,
  syncLogId = null
}: SincronizacaoDivergenciasProps): ReactElement {
  const divergences = catalogDivergencesOf(dataset);
  if (divergences === null) {
    return (
      <section className="page__section flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1" data-section="divergencias">
        <h3 className="text-lg font-extrabold text-ink">Divergências Bling↔local</h3>
        <StatusPanel kind="awaiting_source" title="Sem o lado operacional não há comparação" detail={blingSourceLabel(dataset.snapshot)} />
      </section>
    );
  }
  const summary = summarizeDivergences(divergences);
  if (syncLogId) {
    const fromEvent = divergencesForSyncLog(divergences, syncLogId);
    return (
      <div data-drilldown="sync-log">
        {syncLogBlock(dataset, syncLogId)}
        <section
          className="page__section flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1"
          data-section="divergencias"
        >
          <h3 className="text-lg font-extrabold text-ink">Divergências originadas por este evento (item a item)</h3>
          <p className="page__note flex flex-wrap items-center gap-2 text-sm text-muted" data-divergence-from-sync-log={fromEvent.length}>
            <span>{`${fromEvent.length} divergência(s) apontam para o evento ${syncLogId}, de ${summary.total} comparada(s).`}</span>
            <Link to="/sincronizacao" className="font-semibold text-brand-text hover:underline">
              Ver a fila inteira de sincronização
            </Link>
            <span aria-hidden>·</span>
            <Link to="/divergencias" className="font-semibold text-brand-text hover:underline">
              Ver todas as divergências
            </Link>
          </p>
          <DivergenciasTable divergences={fromEvent} bare />
        </section>
      </div>
    );
  }
  const pending = divergences.filter((divergence) => divergence.severity !== "expected");
  return (
    <section className="page__section flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1" data-section="divergencias">
      <h3 className="text-lg font-extrabold text-ink">Divergências Bling↔local (item a item)</h3>
      <p className="page__note text-sm text-muted" data-divergence-pending={pending.length}>
        {`${pending.length} divergência(s) a tratar de ${summary.total} comparada(s). ` +
          `As ${summary.expected} divergência(s) esperada(s) do catálogo em duas camadas ficam em `}
        <Link to="/divergencias" className="font-semibold text-brand-text hover:underline">
          /divergencias
        </Link>
        .
      </p>
      <DivergenciasTable divergences={pending} bare />
    </section>
  );
}

/** A mesma seção, carregando o dataset de catálogo (uso na página real). */
export function SincronizacaoDivergenciasSection({
  syncLogId = null
}: {
  syncLogId?: string | null;
}): ReactElement {
  return (
    <CatalogBoundary>
      {(catalog: CatalogDataset) => <SincronizacaoDivergenciasView dataset={catalog} syncLogId={syncLogId} />}
    </CatalogBoundary>
  );
}

export function SincronizacaoView({
  dataset,
  kpiId,
  now,
  divergencesSection,
  syncLogId = null
}: SincronizacaoViewProps): ReactElement {
  const notifications = notificationsForKpi(dataset, kpiId, now ?? new Date());
  return (
    <div className="page page--sincronizacao flex flex-col gap-6" data-page="sincronizacao" data-sync-log={syncLogId ?? undefined}>
      <DrillDownHeader title="Falhas de sincronização" kpiId={kpiId} recordCount={notifications.length} recordNoun="alerta(s)" />
      {syncLogId ? (
        <p className="page__note text-sm text-muted" data-origin="sync-log">
          {`Você chegou pelo evento de sync ${syncLogId}: o registro de origem e as divergências que ele explica estão na seção do evento, abaixo.`}
        </p>
      ) : null}
      <p className="page__note text-sm text-muted">
        O indicador conta apenas estes alertas de integração. As divergências de catálogo item a item estão na seção abaixo e em{" "}
        <Link to="/divergencias" className="font-semibold text-brand-text hover:underline">
          /divergencias
        </Link>
        .
      </p>
      <section className="page__section flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1" data-section="alertas">
        <h3 className="text-lg font-extrabold text-ink">Alertas de integração</h3>
        <RecordTable<PlatformNotification>
          bare
          caption="Alertas de sincronização que compõem o indicador"
          rows={notifications}
          idOf={(notification) => notification.id}
          emptyMessage="Nenhum alerta de sincronização aberto."
          // Fase 10 (Plano de Ataque R8): rollout mecânico do primitivo da Fase 6.
          animateRows
          columns={[
            { key: "title", label: "Alerta", render: (notification) => notification.title },
            { key: "type", label: "Tipo", render: (notification) => <code className="text-xs text-muted">{notification.type}</code> },
            {
              key: "severity",
              label: "Severidade",
              render: (notification) => <Badge tone={SEVERITY_TONE[notification.severity] ?? "neutral"}>{notification.severity}</Badge>
            },
            {
              key: "entity",
              label: "Registro relacionado",
              render: (notification) =>
                notification.related_entity_type
                  ? `${notification.related_entity_type}:${notification.related_entity_id ?? "—"}`
                  : "—"
            },
            { key: "createdAt", label: "Detectado em", render: (notification) => formatDateTime(notification.created_at) },
            { key: "body", label: "Detalhe", render: (notification) => notification.body || "—" }
          ]}
        />
      </section>
      {divergencesSection ?? null}
    </div>
  );
}

export function Sincronizacao(): ReactElement {
  const [searchParams] = useSearchParams();
  const kpiId = parseKpiId(searchParams.get("kpi"));
  const syncLogId = parseSyncLogId(searchParams.get("syncLog"));
  return (
    <DatasetBoundary>
      {(dataset: DashboardDataset) => (
        <SincronizacaoView
          dataset={dataset}
          kpiId={kpiId}
          syncLogId={syncLogId}
          divergencesSection={<SincronizacaoDivergenciasSection syncLogId={syncLogId} />}
        />
      )}
    </DatasetBoundary>
  );
}
